import { Link, useNavigate, useLocation } from "react-router-dom";
import { LogOut, Crown } from "lucide-react";
import { useClientAuth } from "@/lib/clientAuth";
import { tierBadgeLabel } from "@/lib/planTier";

export default function PortalShell({ children, tier = "none" }) {
    const { client, logout } = useClientAuth();
    const navigate = useNavigate();
    const { pathname } = useLocation();

    const tierBadge =
        tier === "sprint" || tier === "studio"
            ? { label: tierBadgeLabel(tier), cls: "border-gold/50 bg-gold/10 text-gold" }
            : null;

    const tabs = [
        { to: "/portal", label: "Overview", match: pathname === "/portal" },
        {
            to: "/portal/invoices",
            label: "Invoices",
            match: pathname.startsWith("/portal/invoices"),
        },
        {
            to: "/portal/help",
            label: "Help",
            match: pathname.startsWith("/portal/help"),
        },
        {
            to: "/portal/account",
            label: "Account",
            match: pathname.startsWith("/portal/account"),
        },
    ];

    return (
        <main data-testid="portal-shell" className="min-h-screen bg-ink text-bone">
            <header className="sticky top-0 z-30 bg-ink/80 backdrop-blur-xl border-b border-white/10">
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
                    <Link to="/portal" className="flex items-center gap-3" data-testid="portal-brand">
                        <img
                            src="/logo.png"
                            alt="Aximo Studios"
                            className="h-9 w-auto"
                        />
                        <span className="font-display text-base tracking-tight text-bone-300 hidden sm:inline">
                            /Portal
                        </span>
                        {tierBadge && (
                            <span
                                data-testid={`portal-tier-badge-${tier}`}
                                className={`hidden md:inline-flex items-center gap-1.5 px-2 py-1 border text-[10px] uppercase tracking-[0.25em] ${tierBadge.cls}`}
                            >
                                <Crown className="w-3 h-3" />
                                {tierBadge.label}
                            </span>
                        )}
                    </Link>

                    <nav className="hidden md:flex items-center gap-2">
                        {tabs.map((t) => (
                            <Link
                                key={t.to}
                                to={t.to}
                                data-testid={`portal-tab-${t.label.toLowerCase()}`}
                                className={`px-4 h-10 inline-flex items-center text-xs uppercase tracking-[0.2em] border transition-colors ${
                                    t.match
                                        ? "border-ember text-ember"
                                        : "border-white/15 text-bone-300 hover:border-white/30 hover:text-bone"
                                }`}
                            >
                                {t.label}
                            </Link>
                        ))}
                    </nav>

                    <div className="flex items-center gap-3">
                        {client && (
                            <span className="hidden sm:block text-xs text-bone-500">
                                {client.email}
                            </span>
                        )}
                        <button
                            onClick={() => {
                                logout();
                                navigate("/");
                            }}
                            data-testid="portal-logout"
                            className="inline-flex items-center gap-2 h-10 px-4 border border-white/15 hover:border-white/30 transition-colors text-xs uppercase tracking-[0.18em]"
                        >
                            <LogOut className="w-3.5 h-3.5" />
                            Sign out
                        </button>
                    </div>
                </div>
            </header>
            <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-10">{children}</div>
        </main>
    );
}
